"use client";

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { MenuIcon } from "./icons";
import styles from "./global-nav.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The nav's client half: the sticky slide-away and the mobile menu disclosure.
 * Everything else the nav renders — logo, links, search and cart, aux row —
 * arrives as an already-rendered slot and stays on the server. An open menu
 * must not be scrolled off screen, so the two share one state machine.
 */

/**
 * The width at which the menu stops existing and the links become a row.
 * ⚠ Mirrors --bp-desktop in tokens/grid.css — matchMedia cannot read a custom
 * property, so the literal is repeated and asserted by global-nav.spec.tsx.
 */
export const DESKTOP_BREAKPOINT = 1280;

/**
 * What one rem is worth inside a media condition: the browser's own default,
 * not the root font size the page computes.
 */
const CONDITION_REM_PX = 16;

export const DESKTOP_QUERY = `(min-width: ${DESKTOP_BREAKPOINT / CONDITION_REM_PX}rem)`;

/** Ignore sub-pixel and rubber-band scrolling; only a deliberate move counts. */
const SCROLL_STEP = 8;

/** The three ways the bar can sit on the page. */
export type StickyMode = "hide-on-scroll" | "pinned" | "off";

export interface NavShellProps {
  /** Rendered as the header's first child, so it is the first thing Tab finds. */
  skipLink?: React.ReactNode;
  aux?: React.ReactNode;
  logo: React.ReactNode;
  /** Links plus sign-in and the phone line — the drawer on mobile. Absent,
      there is nothing to disclose, so no menu button is rendered either. */
  panel?: React.ReactNode;
  /** Search and cart; these stay in the bar at every width. */
  controls: React.ReactNode;
  /** Reporting attributes for the menu button. Passed in rather than built
   *  here so this client file need not pull in the analytics module for one
   *  string. Attributes only — the button already has a handler, and a push
   *  beside it would report the press twice. */
  menuTracking?: Record<string, string>;
  /**
   * How the bar rides the page. `pinned` stays put at every scroll position;
   * `off` renders a plain header and registers no scroll listener at all.
   */
  sticky: StickyMode;
}

/**
 * Every control in the nav that opens an app-owned popover — today the bar's
 * search and cart. They arrive as server-rendered slot content, so there is no
 * ref to hold them, and the attribute that makes them work is what identifies
 * them. ⚠ The scope is the whole header, not just the bar: an invoker added to
 * the aux strip would be picked up here too.
 */
function panelInvokers(header: HTMLElement) {
  return Array.from(header.querySelectorAll<HTMLElement>("[popovertarget]"));
}

const OPEN_MENU_LABEL = "Open menu";
const CLOSE_MENU_LABEL = "Close menu";
const MENU_LABEL = "Menu";

function NavShellImpl({
  skipLink,
  aux,
  logo,
  panel,
  controls,
  menuTracking,
  sticky
}: NavShellProps) {
  const [menuOpenState, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  /* Derived, not just read: if the panel is withdrawn while the menu is open,
     raw state would keep the scroll locked and `inert` applied for a drawer
     that no longer exists. */
  const hasPanel = panel !== null && panel !== undefined;
  const menuOpen = menuOpenState && hasPanel;

  /* Clears the STORED intent as well, or a panel that returns re-opens the
     drawer with no user action. */
  useEffect(() => {
    if (!hasPanel) {
      setMenuOpen(false);
    }
  }, [hasPanel]);

  const headerRef = useRef<HTMLElement>(null);
  /* Measured on mount and on resize: reading offsetHeight inside the scroll
     callback forces a layout flush every frame. */
  const barHeight = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = `${useId()}-menu`;

  /* ⚠ Restoring focus to the trigger is right for a dismissal (Escape) and
     wrong for a link follow: there the browser is moving focus to the
     destination, and pulling it back strands a keyboard user. */
  const closeMenu = useCallback((restoreFocus = true) => {
    setMenuOpen(false);
    if (restoreFocus) {
      triggerRef.current?.focus({ preventScroll: true });
    }
  }, []);

  /* Following a link has to dismiss the drawer, or a same-page href leaves it
     covering the thing it just scrolled to with the page still locked. One
     delegated handler, because the links are server-rendered. */
  const onPanelClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Not redundant: above the breakpoint this element is `display: contents`,
    // so without the guard every desktop pill click would write menu state.
    if (menuOpen && (event.target as HTMLElement).closest("a[href]")) {
      closeMenu(false);
    }
  };

  useEffect(() => {
    // Pinned still reads the scroll position for the shadow, but never moves
    // the bar, so it needs neither the bar height nor the resize listener.
    const hides = sticky === "hide-on-scroll";

    // `sticky` can change under a live component, and whatever the previous
    // mode left behind has to go or the bar stays parked off-screen.
    if (!hides) {
      setHidden(false);
    }

    if (sticky === "off") {
      setScrolled(false);
      return undefined;
    }

    let last = window.scrollY;
    let frame = 0;

    const measure = () => {
      barHeight.current = headerRef.current?.offsetHeight ?? 0;
    };

    const read = () => {
      frame = 0;
      const y = window.scrollY;
      setScrolled(y > 0);

      if (!hides || Math.abs(y - last) < SCROLL_STEP) {
        return;
      }
      // Never hide while the bar is still over the top of the document —
      // otherwise a short page can slide it away and never bring it back.
      const past = y > barHeight.current;
      setHidden(y > last && past);
      last = y;
    };

    const onScroll = () => {
      if (!frame) {
        frame = requestAnimationFrame(read);
      }
    };

    // Through the same frame guard as the scroll path: offsetHeight is a forced
    // layout read, and a drag-resize fires this continuously.
    const onResize = () => {
      if (!frame) {
        frame = requestAnimationFrame(() => {
          measure();
          read();
        });
      }
    };

    if (hides) {
      measure();
      window.addEventListener("resize", onResize);
    }
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [sticky]);

  useEffect(() => {
    const header = headerRef.current;
    const panel = panelRef.current;
    const trigger = triggerRef.current;

    if (!menuOpen || !header || !panel || !trigger) {
      return undefined;
    }

    /* ⚠ A focus trap only stops Tab; a screen reader's virtual cursor reads
       straight through the drawer into the page behind, so `inert` is what
       takes that content out of the accessibility tree. The walk starts at
       body and skips the ancestor path of the panel and its trigger — the
       trigger stays live because it is the control that closes this.

       The bar's panel invokers stay live for the same reason: the frame draws
       search and cart at full opacity beside the open drawer, and a control
       that looks pressable has to be pressable. */
    const invokers = panelInvokers(header);
    const live: Element[] = [panel, trigger, ...invokers];

    const marked: Element[] = [];
    const walk = (node: Element) => {
      Array.from(node.children).forEach(child => {
        if (live.indexOf(child) !== -1) {
          return;
        }
        if (live.some(el => child.contains(el))) {
          walk(child);
          return;
        }
        // ⚠ Leave pre-existing inert alone: a mark the host app made must
        // survive this cycle, so only what this walk marked is ever unmarked.
        if (child.hasAttribute("inert")) {
          return;
        }
        child.setAttribute("inert", "");
        marked.push(child);
      });
    };

    walk(document.body);

    /* ⚠ Unmarked HERE, synchronously, rather than left to this effect's
       cleanup: the browser opens the popover once the click has finished
       dispatching, and a React state change is not guaranteed to have run by
       then. A panel opened under an inert ancestor cannot take focus.
       It empties as it goes, so the cleanup's second call releases nothing
       the host may have re-marked in between. */
    const release = () => marked.splice(0).forEach(el => el.removeAttribute("inert"));

    const onInvoke = (event: Event) => {
      release();
      /* Focus is on a drawer row about to become `display: none`, and not
         every browser gives focus to the button it was clicked with. Park it
         on the control; a panel that autofocuses its own field takes it from
         here. Not the hamburger: this press is not a dismissal. */
      (event.currentTarget as HTMLElement).focus({ preventScroll: true });
      closeMenu(false);
    };

    invokers.forEach(el => el.addEventListener("click", onInvoke));

    return () => {
      invokers.forEach(el => el.removeEventListener("click", onInvoke));
      release();
    };
  }, [menuOpen, closeMenu]);

  // The menu only exists below the desktop breakpoint; crossing above it while
  // open would leave the page scroll locked with nothing on screen to explain.
  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    // Guarded because jsdom ships no matchMedia: unguarded, opening the menu
    // throws in any consumer's test rather than in ours.
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => {
      if (desktop.matches) {
        setMenuOpen(false);
      }
    };
    onChange();
    desktop.addEventListener("change", onChange);
    return () => desktop.removeEventListener("change", onChange);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    /* The drawer hangs off the bottom of the bar, so its height is the gap
       below the bar rather than a constant. Anything above the nav pushes the
       bar down, and a constant then runs the drawer's last rows off a frozen
       page that neither it nor the oversized drawer will scroll. */
    const header = headerRef.current;
    const panelEl = panelRef.current;
    let frame = 0;
    const fit = () => {
      frame = 0;
      const bottom = header?.getBoundingClientRect().bottom ?? 0;
      panelEl?.style.setProperty("--panel-h", `${Math.max(0, window.innerHeight - bottom)}px`);
    };
    const onResize = () => {
      if (!frame) {
        frame = requestAnimationFrame(fit);
      }
    };

    // Measure before the overflow write: read-then-write, or the rect read
    // lands after a style write in the same click task and forces a layout.
    fit();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("resize", onResize);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("resize", onResize);
      if (frame) {
        cancelAnimationFrame(frame);
      }
      panelEl?.style.removeProperty("--panel-h");
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    /* Array.from, not a spread: iterating a NodeList needs the dom.iterable
       lib, which this tsconfig only gets from a test-only @types package. */
    const focusables = () => {
      const found = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      return Array.from(found ?? []).filter(el => el.offsetParent !== null);
    };

    focusables()[0]?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      // The trigger stays reachable: it is the only way back out, and it sits
      // outside the panel in the bar above it.
      const stops = [...focusables(), triggerRef.current].filter(Boolean) as HTMLElement[];
      if (stops.length === 0) {
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen, closeMenu]);

  return (
    <header
      className={styles.header}
      data-hidden={hidden && !menuOpen ? "true" : undefined}
      data-menu-open={menuOpen ? "true" : undefined}
      data-scrolled={scrolled ? "true" : undefined}
      data-static={sticky === "off" ? "true" : undefined}
      ref={headerRef}
    >
      {skipLink}
      {aux}

      <div className={styles.bar}>
        {logo}

        {/* The role is conditional because above the breakpoint this element is
            `display: contents` and holds the desktop link row. ⚠ aria-modal is
            deliberately NOT set: the trigger sits outside the panel and is the
            way back out, so modality would tell a screen reader to ignore it. */}
        {hasPanel && (
          <div
            aria-label={menuOpen ? MENU_LABEL : undefined}
            className={styles.panel}
            id={panelId}
            onClick={onPanelClick}
            ref={panelRef}
            role={menuOpen ? "dialog" : undefined}
          >
            {panel}
          </div>
        )}

        {controls}

        {hasPanel && (
          <button
            /* Spread FIRST so this component's own props win. */
            {...menuTracking}
            aria-controls={panelId}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? CLOSE_MENU_LABEL : OPEN_MENU_LABEL}
            className={`${styles.iconButton} ${styles.menuButton}`}
            onClick={() => setMenuOpen(open => !open)}
            ref={triggerRef}
            type="button"
          >
            <MenuIcon />
          </button>
        )}
      </div>
    </header>
  );
}
export const NavShell = withSafeRender(NavShellImpl, "NavShell");

export default NavShell;
